<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Button from '../components/Button.svelte';
  import Async from '../components/Async.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getDashboard, getDashboardPartners, rebuildDashboard } from '../lib/api.js';
  import { mayVisit, currentRole } from '../lib/role.svelte.js';
  import { HELP } from '../lib/help.js';

  async function loadDashboard() {
    const local = await getDashboard();
    const summary = local.summary;
    return { ...local,
      records: summary?.records.total ?? null,
      periods: summary?.records.periods ?? [],
      recordContainers: summary?.records.containers ?? [],
      aggregateCount: summary?.aggregates.configured ?? null,
    };
  }
  let dash = $state(loadDashboard());
  let rebuilding = $state(false);
  let rebuildError = $state(null);

  async function rebuildStatistics() {
    rebuilding = true;
    rebuildError = null;
    try {
      await rebuildDashboard();
      dash = loadDashboard();
    } catch (error) {
      rebuildError = error.message;
    } finally {
      rebuilding = false;
    }
  }

  // Each card belongs to a role's work (roles.md). Show only the cards this
  // role can actually act on — a card whose buttons all lead nowhere is worse
  // than no card, because it implies access the router will refuse.
  const showConnector = $derived(mayVisit('connector'));
  const showRecords = $derived(mayVisit('records'));
  const showDisclosure = $derived(mayVisit('disclosure'));
  const showPartners = $derived(mayVisit('partners'));
  const showCatalog = $derived(mayVisit('catalog'));
  const showAnalysis = $derived(mayVisit('analysis'));

  // Drop a column entirely when this role has nothing in it, so the remaining
  // one isn't stranded beside a blank half.
  const showLeft = $derived(showConnector || showDisclosure || showRecords);
  const showRight = $derived(showPartners || showCatalog || showAnalysis);

  // Remote health is deliberately independent and only requested for roles
  // whose dashboard actually renders it.
  const partnerHealth = $derived.by(() => (showPartners || showCatalog)
    ? getDashboardPartners()
    : Promise.resolve({ partners: [], counts: { ownFiles: 0, partnerFiles: 0 } }));
</script>

<HelpDrawer {...HELP.dashboard} />

<h1 class="wf2-title wf2-title-level-1">Dashboard</h1>
<p class="wf2-subtitle">
  Signed in as <strong>{currentRole()?.label}</strong>. Open a card to do your
  work — only the parts of the Pod your role acts on are shown.
</p>

<Async promise={dash}>
  {#snippet children(d)}
    {@const periodRange = d.periods.length ? `${d.periods[0]} – ${d.periods.at(-1)}` : '—'}
    {#if d.freshness.state !== 'current'}
      <div class="state error">
        Dashboard statistics are {d.freshness.state}.
        {#if showConnector}
          <Button onclick={rebuildStatistics} disabled={rebuilding}>{rebuilding ? 'Rebuilding…' : 'Rebuild statistics'}</Button>
        {:else}
          Ask the technical owner to rebuild them.
        {/if}
        {#if rebuildError} {rebuildError}{/if}
      </div>
    {/if}
    <div class="wf2-row">
      <!-- Left column -->
      {#if showLeft}
      <div class="wf2-col wf2-flexitem">
        {#if showDisclosure || showConnector}
        <Section title="Sources">
          <Panel>
            {#if d.sources.length}
              <p class="wf2-text">{d.sources.length} configured sources · {d.aggregateCount ?? '—'} institution-wide cubes.</p>
              <div class="wf2-tablewrap"><table class="wf2-table"><thead><tr><th>Source</th><th>Mode</th><th>Rows</th><th>Records</th><th>Pending/state</th><th></th></tr></thead><tbody>
                {#each d.sources as source}<tr>
                  <td>{source.state?.label ?? source.connector.sourceLabel ?? source.sourceId}<span class="col-stats mono">{source.sourceId}</span></td>
                  <td>{source.state?.mode ?? '—'}</td><td>{source.state?.rowsInSource ?? '—'}</td><td>{source.state?.records ?? '—'}</td>
                  <td>{source.state ? (source.state.mode === 'snapshot' ? (source.state.watermark ? `checked ${source.state.watermark}` : 'not checked') : `${source.state.pending} pending`) : 'statistics unavailable'}</td>
                  <td><div class="wf2-row" style="gap:6px; flex-wrap:wrap">
                    {#if showDisclosure}<Button variant="ghost" to={`disclosure/label?source=${encodeURIComponent(source.sourceId)}`}>Assess</Button>{/if}
                    {#if showConnector}<Button variant="ghost" to={`connector/run?source=${encodeURIComponent(source.sourceId)}`}>Ingest</Button><Button variant="ghost" to={`connector?source=${encodeURIComponent(source.sourceId)}`}>Configure</Button>{/if}
                    {#if showRecords}<Button variant="ghost" to={`records?source=${encodeURIComponent(source.sourceId)}`}>Records</Button>{/if}
                  </div></td>
                </tr>{/each}
              </tbody></table></div>
            {:else}
              <p class="wf2-text">No source has been imported yet. The technical owner can stage the first CSV; then return here to label its columns.</p>
              {#if showConnector}<Button variant="primary" to="connector">Import your first dataset</Button>{/if}
            {/if}
          </Panel>
        </Section>
        {/if}

        {#if showRecords}
        <Section title="Records">
          <Panel>
            <DataTable
              columns={['Field', 'Value']}
              rows={[
                ['Records in Pod', d.records === null ? 'statistics unavailable' : `${d.records}`],
                ['Periods covered', periodRange],
                ['Containers', d.recordContainers.join(', ') || 'not configured'],
              ]}
            />
            <Button variant="primary" to="records">Browse records</Button>
          </Panel>
        </Section>
        {/if}
      </div>
      {/if}

      <!-- Right column -->
      {#if showRight}
      <div class="wf2-col wf2-flexitem">
        {#if showPartners}
        <Section title="Partners">
          <Panel>
            <Async promise={partnerHealth} loading="Checking partner connections…">
              {#snippet children(remote)}
                <p class="wf2-subtitle">{remote.partners.length} in the registry · checked {new Date(remote.observedAt).toLocaleTimeString()}</p>
                <DataTable
                  columns={['Partner', 'Inbound', 'Status']}
                  rows={remote.partners.map((p) => [
                    p.label,
                    p.inboundCount ? `${p.readableCount} of ${p.inboundCount} readable` : 'nothing shared yet',
                    p.readableCount ? 'Sharing both ways' : p.inboundCount ? 'Waiting on them' : 'You share, they don’t',
                  ])}
                />
              {/snippet}
            </Async>
            <div class="wf2-row" style="gap:12px">
              <Button variant="primary" to="partners">Manage partners</Button>
              <Button variant="secondary" to="partner">Add partner</Button>
            </div>
          </Panel>
        </Section>
        {/if}

        {#if showCatalog}
        <Section title="Catalog">
          <Panel>
            <Async promise={partnerHealth} loading="Loading shared catalog status…">
              {#snippet children(remote)}
                <DataTable
                  columns={['Institution', 'Shared theme', 'Cube files']}
                  rows={[
                    ['This institution', 'configured aggregates', `${remote.counts.ownFiles}`],
                    ...remote.partners.map((p) => [
                      p.label,
                      p.readableCount ? 'diversity' : p.inboundCount ? 'pending' : 'nothing shared yet',
                      `${remote.counts.partnerFiles}`,
                    ]),
                  ]}
                />
              {/snippet}
            </Async>
            <Button variant="secondary" to="catalog">Browse catalog</Button>
          </Panel>
        </Section>
        {/if}

        {#if showAnalysis}
        <Section title="Comparisons">
          <Panel>
            <p class="wf2-text">
              Each institution's figures side by side. You and your partners
              record things like gender in your own words — the comparison keeps
              both, rather than making anyone change theirs.
            </p>
            <div class="wf2-row" style="gap:12px">
              <Button variant="primary" to="analysis">View comparisons</Button>
              <Button variant="secondary" to="queries">Queries</Button>
            </div>
          </Panel>
        </Section>
        {/if}
      </div>
      {/if}
    </div>
  {/snippet}
</Async>
